import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';

export interface PortalProduct {
  /** Stable id, matched against User.allowedProducts for entitlement gating. */
  id:     string;
  label:  string;
  /** Identifier of the inline SVG to render (see template <ng-container [ngSwitch]>). */
  icon:   'go-live' | 'planning-suite';
  /** External portal URL. Null for the product we're currently inside (Planning Suite). */
  url:    string | null;
  /** True for the product this app represents — gets the active highlight. */
  active: boolean;
}

@Component({
  selector: 'sp-side-nav',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent {
  /** Collapsed (icon-only rail) by default; not persisted across loads. */
  expanded = false;

  /**
   * Full product catalogue in display order. Go-Live links out to the Go Live
   * portal; Planning Suite is the current (active) product. Go-Live is gated
   * by department (see `showGoLive`); only the active product is highlighted.
   */
  private readonly allProducts: PortalProduct[] = [
    { id: 'go-live',        label: 'Go-Live',        icon: 'go-live',        url: 'https://acm.silverpush.co/go-live', active: false },
    { id: 'planning-suite', label: 'Planning Suite', icon: 'planning-suite', url: null,                                active: true  },
  ];

  /** Departments (display_department_name) entitled to the Go-Live product. */
  private readonly goLiveDepartments = ['admin', 'viewer planner', 'campaign manager planner'];

  constructor(private auth: AuthService) {}

  /**
   * Products shown in the rail. The active product (Planning Suite) is always
   * visible; Go-Live is shown only for entitled departments (see showGoLive).
   */
  get products(): PortalProduct[] {
    return this.allProducts.filter(p => p.active || (p.id === 'go-live' && this.showGoLive));
  }

  /**
   * Whether to show the Go-Live product. Gated on the human-facing department
   * label. When absent (local/dev bypassAuth — no department on the user),
   * show it, mirroring the product-switcher "absent → show" convention;
   * real users always carry the field, so production is strict to the
   * three entitled departments.
   */
  get showGoLive(): boolean {
    const dept = this.auth.snapshot?.displayDepartment;
    if (dept == null) return true;
    return this.goLiveDepartments.includes(dept.trim().toLowerCase());
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  selectProduct(product: PortalProduct): void {
    if (product.active || !product.url) return;
    window.location.href = product.url;
  }
}
